import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export default function AdminChangePassword() {
  const navigate = useNavigate();
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const strength = (() => {
    const checks = [
      newPwd.length >= 8,
      /[a-z]/.test(newPwd),
      /[A-Z]/.test(newPwd),
      /\d/.test(newPwd),
      /[^a-zA-Z0-9]/.test(newPwd),
    ];
    const score = checks.filter(Boolean).length;
    return {
      score,
      label: ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte', 'Excelente'][score],
      color: ['bg-red-500', 'bg-red-500', 'bg-amber-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-600'][score],
    };
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (newPwd !== confirmPwd) { toast.error('Las contraseñas no coinciden'); return; }
    if (newPwd === currentPwd) { toast.error('La nueva contraseña debe ser distinta de la actual'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/me/change-password`, {
        current_password: currentPwd, new_password: newPwd,
      }, { headers: authHeaders() });
      toast.success('Contraseña actualizada correctamente');
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(' · ') : (detail || 'Error al cambiar contraseña');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#009940] to-[#69C7EA] flex items-center justify-center shadow-lg mb-3">
            <KeyRound size={26} className="text-white" />
          </div>
          <h1 className="text-xl font-semibold text-[#374B67]">Cambiar contraseña</h1>
          <p className="text-sm text-slate-500 mt-1">Establece una nueva contraseña para tu cuenta.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 space-y-4">
          <div>
            <Label className="text-xs text-slate-600">Contraseña actual</Label>
            <Input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
              required data-testid="chg-current-pwd" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nueva contraseña</Label>
            <div className="relative">
              <Input type={show ? 'text' : 'password'} value={newPwd} onChange={e => setNewPwd(e.target.value)}
                required minLength={8} data-testid="chg-new-pwd" />
              <button type="button" onClick={() => setShow(!show)} aria-label="Mostrar contraseña"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {newPwd && (
              <div className="mt-2">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${strength.color} transition-all duration-200`} style={{ width: `${(strength.score / 5) * 100}%` }} />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Fortaleza: <strong>{strength.label}</strong></p>
              </div>
            )}
            <ul className="mt-2 text-[11px] text-slate-500 space-y-0.5">
              <li className={newPwd.length >= 8 ? 'text-green-700' : ''}>{newPwd.length >= 8 ? '✓' : '•'} Al menos 8 caracteres</li>
              <li className={/\d/.test(newPwd) && /[a-zA-Z]/.test(newPwd) ? 'text-green-700' : ''}>
                {/\d/.test(newPwd) && /[a-zA-Z]/.test(newPwd) ? '✓' : '•'} Combina letras y números
              </li>
            </ul>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Confirmar nueva contraseña</Label>
            <Input type={show ? 'text' : 'password'} value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
              required minLength={8} data-testid="chg-confirm-pwd"
              className={confirmPwd && newPwd !== confirmPwd ? 'border-red-300 focus-visible:ring-red-300' : ''} />
            {confirmPwd && newPwd !== confirmPwd && <p className="text-[11px] text-red-600 mt-1">Las contraseñas no coinciden</p>}
          </div>
          <Button type="submit" disabled={saving}
            className="w-full bg-[#009940] hover:bg-[#007F35] text-white"
            data-testid="chg-submit-button">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Guardando…</> : <><CheckCircle2 size={16} className="mr-2" /> Guardar nueva contraseña</>}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
}
